import React, { useState, useEffect } from 'react';
import { BookService } from '../../book/services/bookService';
import { stripHtml } from '../../../lib/html-text';

interface AddBooksModalProps {
  collectionId: string;
  existingBookIds: string[];
  onClose: () => void;
  onAdd: (bookIds: string[]) => Promise<void>;
}

const AddBooksModal: React.FC<AddBooksModalProps> = ({
  collectionId,
  existingBookIds,
  onClose,
  onAdd
}) => {
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookIds, setSelectedBookIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      setLoading(true);
      const response = await BookService.getBooks();
      
      if (response && response.data) {
        const booksData = response.data || response.result || response;
        setBooks(Array.isArray(booksData) ? booksData : []);
      }
    } catch (err) {
      console.error('Failed to load books:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBook = (bookId: string) => {
    setSelectedBookIds(prev => 
      prev.includes(bookId)
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const handleAdd = async () => {
    if (selectedBookIds.length === 0) return;

    try {
      setAdding(true);
      await onAdd(selectedBookIds);
    } catch (err) {
      console.error('Failed to add books:', err);
    } finally {
      setAdding(false);
    }
  };

  // Filter books that are not already in the collection
  const availableBooks = books.filter(book => 
    !existingBookIds.includes(book.id || book._id)
  );

  // Apply search filter
  const filteredBooks = availableBooks.filter(book => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (book.title || book.name || '').toLowerCase().includes(query) ||
      stripHtml(book.description).toLowerCase().includes(query) ||
      (book.id || book._id || '').toLowerCase().includes(query)
    );
  });

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Thêm sách vào bộ sách</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="mt-4 mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Tìm kiếm sách..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Books List */}
        <div className="mt-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-blue-600 rounded-full"></div>
              <p className="mt-2 text-gray-600">Đang tải danh sách sách...</p>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="text-center py-8">
              <svg className="h-12 w-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <p className="text-gray-500">
                {searchQuery ? 'Không tìm thấy sách phù hợp' : 'Không có sách nào để thêm'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBooks.map((book) => {
                const bookId = book.id || book._id;
                const isSelected = selectedBookIds.includes(bookId);

                return (
                  <div
                    key={bookId}
                    onClick={() => handleToggleBook(bookId)}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-blue-300'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {book.title || book.name || 'Không có tiêu đề'}
                      </div>
                      {stripHtml(book.description) && (
                        <div className="text-sm text-gray-500 line-clamp-1">
                          {stripHtml(book.description)}
                        </div>
                      )}
                    </div>
                    {book.status && (
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        book.status === 'published' || book.status === 'Đã xuất bản'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {book.status === 'published' ? 'Đã xuất bản' : book.status === 'draft' ? 'Nháp' : book.status}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected Count */}
        {selectedBookIds.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Đã chọn <strong>{selectedBookIds.length}</strong> sách
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={adding}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Hủy
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedBookIds.length === 0 || adding}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Đang thêm...
              </>
            ) : (
              `Thêm ${selectedBookIds.length} sách`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBooksModal;

